import React from "react";
import { ImageBackground, StyleSheet, Text, View } from "react-native";
import { CF } from "../../theme/turquoiseTheme.js";

import atomicStairs from "../../assets/library/atomic_stairs.png";
import dopamineSplit from "../../assets/library/dopamine_split.png";
import habitTree from "../../assets/library/habit_tree.png";
import icyCliff from "../../assets/library/icy_cliff.png";
import focusCube from "../../assets/library/focus_cube.png";
import urgeBrain from "../../assets/library/urge_brain.png";
import urgeSurf from "../../assets/library/urge_surf.png";
import identityDoor from "../../assets/library/identity_door.png";
import envSplit from "../../assets/library/env_split.png";
import journeyPath from "../../assets/library/journey_path.png";

const SCENE_IMAGES = {
  atomic_stairs: atomicStairs,
  dopamine_split: dopamineSplit,
  habit_tree: habitTree,
  icy_cliff: icyCliff,
  focus_cube: focusCube,
  urge_brain: urgeBrain,
  urge_surf: urgeSurf,
  identity_door: identityDoor,
  env_split: envSplit,
  journey_path: journeyPath,
};

const PAL = {
  gold: "#F5C542",
  goldSoft: "#FDE68A",
  sunrise: "#FF8A65",
  cyan: CF.cyan,
  teal: CF.primaryBright,
  deep: "#050A18",
  navy: "#0A1628",
  purple: CF.purpleAccent,
  white: "#FFFFFF",
  chaos: "#FB7185",
  calm: "#5EEAD4",
};

/**
 * Cinematic transformation artwork — painted hero images with procedural fallback.
 * @param {{ sceneId: string, height?: number, overlay?: string }} props
 */
export function CinematicScene({ sceneId, height = 220, overlay }) {
  const raw = SCENE_IMAGES[sceneId] ?? SCENE_IMAGES.journey_path;
  const imageSource = typeof raw === "string" ? { uri: raw } : raw;
  const render = SCENE_RENDERERS[sceneId] ?? SCENE_RENDERERS.journey_path;

  return (
    <View style={[styles.frame, { height }]}>
      {imageSource ? (
        <ImageBackground source={imageSource} style={styles.imageFill} resizeMode="cover" imageStyle={styles.imageStyle}>
          <View style={styles.vignetteBottom} pointerEvents="none" />
          {overlay ? (
            <View style={styles.overlayWrap} pointerEvents="none">
              <Text style={styles.overlayText}>{overlay}</Text>
            </View>
          ) : null}
        </ImageBackground>
      ) : (
        <>
          {render(height)}
          <View style={styles.vignetteBottom} pointerEvents="none" />
          {overlay ? (
            <View style={styles.overlayWrap} pointerEvents="none">
              <Text style={styles.overlayText}>{overlay}</Text>
            </View>
          ) : null}
        </>
      )}
    </View>
  );
}

/** @param {number} h */
function AtomicStairs(h) {
  const steps = [0.18, 0.28, 0.38, 0.5, 0.62, 0.74, 0.86];
  return (
    <View style={[styles.fill, { backgroundColor: PAL.deep }]}>
      <View style={[styles.sunrise, { top: h * 0.06 }]} />
      <View style={[styles.sunCore, { top: h * 0.1 }]} />
      {steps.map((y, i) => (
        <View
          key={i}
          style={[
            styles.step,
            {
              bottom: h * y,
              left: `${8 + i * 10}%`,
              width: `${28 + i * 4}%`,
              opacity: 0.55 + i * 0.07,
              borderColor: i > 3 ? PAL.gold : PAL.teal,
              backgroundColor: i > 4 ? "rgba(245, 197, 66, 0.35)" : "rgba(45, 212, 191, 0.22)",
            },
          ]}
        />
      ))}
      <View style={[styles.traveler, { bottom: h * 0.14, left: "12%" }]} />
    </View>
  );
}

function DopamineSplit(h) {
  return (
    <View style={[styles.fill, { flexDirection: "row" }]}>
      <View style={[styles.half, { backgroundColor: "#1A0A14" }]}>
        {[...Array(8)].map((_, i) => (
          <View
            key={i}
            style={{
              position: "absolute",
              top: `${10 + (i % 4) * 22}%`,
              left: `${8 + (i % 3) * 28}%`,
              width: i % 2 ? 18 : 24,
              height: i % 2 ? 28 : 18,
              borderRadius: 4,
              backgroundColor: i % 3 ? PAL.chaos : "#F97316",
              opacity: 0.65,
              transform: [{ rotate: `${-12 + i * 7}deg` }],
            }}
          />
        ))}
        <View style={[styles.chaosGlow, { backgroundColor: "rgba(251, 113, 133, 0.25)" }]} />
      </View>
      <View style={[styles.half, { backgroundColor: "#061A1E" }]}>
        <View style={[styles.calmSun, { top: h * 0.12, right: "18%" }]} />
        <View style={[styles.calmLine, { top: h * 0.45, right: "12%", width: "55%" }]} />
        <View style={[styles.calmLine, { top: h * 0.58, right: "20%", width: "40%", opacity: 0.6 }]} />
        <View style={[styles.plant, { bottom: h * 0.15, right: "22%" }]} />
      </View>
      <View style={[styles.centerFigure, { top: h * 0.42 }]} />
      <View style={styles.centerLine} />
    </View>
  );
}

function HabitTree(h) {
  return (
    <View style={[styles.fill, { backgroundColor: "#061218" }]}>
      <View style={[styles.treeCanopy, { top: h * 0.08 }]} />
      <View style={[styles.treeTrunk, { top: h * 0.38, height: h * 0.35 }]} />
      {[
        { left: "18%", rot: "-35deg", len: h * 0.22 },
        { left: "32%", rot: "-15deg", len: h * 0.28 },
        { left: "48%", rot: "0deg", len: h * 0.32 },
        { left: "62%", rot: "18deg", len: h * 0.26 },
        { left: "76%", rot: "38deg", len: h * 0.2 },
      ].map((r, i) => (
        <View
          key={i}
          style={[
            styles.root,
            {
              left: r.left,
              bottom: h * 0.08,
              width: 3,
              height: r.len,
              transform: [{ rotate: r.rot }],
              shadowColor: PAL.teal,
            },
          ]}
        />
      ))}
      <View style={[styles.rootGlow, { bottom: h * 0.05 }]} />
    </View>
  );
}

function IcyCliff(h) {
  return (
    <View style={[styles.fill, { backgroundColor: "#0B1528" }]}>
      <View style={[styles.mountain, { height: h * 0.85 }]} />
      <View style={[styles.snowDrift, { top: h * 0.22 }]} />
      <View style={[styles.snowDrift, { top: h * 0.38, left: "35%", width: "45%", opacity: 0.5 }]} />
      {[...Array(12)].map((_, i) => (
        <View
          key={i}
          style={{
            position: "absolute",
            top: `${5 + (i * 17) % 70}%`,
            left: `${(i * 23) % 90}%`,
            width: 2,
            height: 2,
            borderRadius: 1,
            backgroundColor: PAL.white,
            opacity: 0.35 + (i % 3) * 0.2,
          }}
        />
      ))}
      <View style={[styles.climber, { top: h * 0.55, left: "38%" }]} />
    </View>
  );
}

function FocusCube(h) {
  return (
    <View style={[styles.fill, { backgroundColor: "#0C1220" }]}>
      {[...Array(6)].map((_, i) => (
        <View
          key={i}
          style={[styles.blurBlock, { top: `${8 + i * 14}%`, left: `${(i * 19) % 75}%`, opacity: 0.25 }]}
        />
      ))}
      <View style={[styles.focusCube, { top: h * 0.22, alignSelf: "center" }]}>
        <View style={styles.cubeInner}>
          <View style={styles.cubeDesk} />
          <View style={styles.cubeGlow} />
        </View>
      </View>
    </View>
  );
}

function UrgeBrain(h) {
  return (
    <View style={[styles.fill, { backgroundColor: "#080E1C", alignItems: "center", justifyContent: "center" }]}>
      <View style={styles.brainShell}>
        <View style={[styles.brainPulse, { width: h * 0.35, height: h * 0.35 }]} />
        <View style={[styles.brainPulse, { width: h * 0.5, height: h * 0.5, opacity: 0.35 }]} />
        <View style={[styles.brainPulse, { width: h * 0.65, height: h * 0.65, opacity: 0.15 }]} />
        <View style={styles.brainCore} />
      </View>
    </View>
  );
}

function UrgeSurf(h) {
  return (
    <View style={[styles.fill, { backgroundColor: "#050C18" }]}>
      <View style={[styles.lightWave, { bottom: h * 0.15 }]} />
      <View style={[styles.lightWave, { bottom: h * 0.22, opacity: 0.45, transform: [{ scaleX: 1.1 }] }]} />
      <View style={[styles.surfSilhouette, { bottom: h * 0.32, left: "42%" }]} />
      {[...Array(5)].map((_, i) => (
        <View
          key={i}
          style={{
            position: "absolute",
            bottom: h * (0.28 + i * 0.04),
            left: `${20 + i * 14}%`,
            width: 24,
            height: 8,
            borderRadius: 4,
            backgroundColor: i % 2 ? PAL.purple : PAL.chaos,
            opacity: 0.35,
          }}
        />
      ))}
    </View>
  );
}

function IdentityDoor(h) {
  return (
    <View style={[styles.fill, { backgroundColor: "#060A14" }]}>
      <View style={[styles.darkPast, { width: "42%" }]} />
      <View style={[styles.brightFuture, { width: "58%", left: "42%" }]} />
      <View style={[styles.doorFrame, { top: h * 0.12, height: h * 0.76 }]} />
      <View style={[styles.walker, { bottom: h * 0.18, left: "44%" }]} />
      <View style={[styles.chain, { bottom: h * 0.2, left: "8%" }]} />
      <View style={[styles.cityGlow, { top: h * 0.2, right: "8%" }]} />
    </View>
  );
}

function EnvSplit(h) {
  return (
    <View style={[styles.fill, { flexDirection: "row" }]}>
      <View style={[styles.half, { backgroundColor: "#140A10" }]}>
        <View style={[styles.messBlock, { top: h * 0.2, left: "12%" }]} />
        <View style={[styles.messBlock, { top: h * 0.45, left: "28%", width: 40, height: 28 }]} />
        <View style={[styles.phoneGlow, { bottom: h * 0.2, left: "20%" }]} />
      </View>
      <View style={[styles.half, { backgroundColor: "#061A1C" }]}>
        <View style={[styles.windowSun, { top: h * 0.1, right: "15%" }]} />
        <View style={[styles.minDesk, { bottom: h * 0.22, right: "18%" }]} />
        <View style={[styles.plant, { bottom: h * 0.18, right: "38%" }]} />
      </View>
      <View style={styles.splitDivider} />
    </View>
  );
}

function JourneyPath(h) {
  return (
    <View style={[styles.fill, { backgroundColor: PAL.navy }]}>
      <View style={[styles.horizonGlow, { top: h * 0.25 }]} />
      <View style={[styles.pathLine, { bottom: h * 0.2 }]} />
      <View style={[styles.traveler, { bottom: h * 0.22, left: "45%" }]} />
    </View>
  );
}

/** @type {Record<string, (h: number) => React.ReactNode>} */
const SCENE_RENDERERS = {
  atomic_stairs: AtomicStairs,
  dopamine_split: DopamineSplit,
  habit_tree: HabitTree,
  icy_cliff: IcyCliff,
  focus_cube: FocusCube,
  urge_brain: UrgeBrain,
  urge_surf: UrgeSurf,
  identity_door: IdentityDoor,
  env_split: EnvSplit,
  journey_path: JourneyPath,
};

const styles = StyleSheet.create({
  frame: { width: "100%", overflow: "hidden", backgroundColor: PAL.deep },
  imageFill: { flex: 1, width: "100%", justifyContent: "flex-end" },
  imageStyle: { width: "100%", height: "100%" },
  fill: { flex: 1, width: "100%" },
  vignetteBottom: {
    position: "absolute",
    left: 0,
    right: 0,
    bottom: 0,
    height: "45%",
    backgroundColor: "rgba(5, 10, 24, 0.55)",
  },
  overlayWrap: {
    position: "absolute",
    left: 14,
    right: 14,
    bottom: 14,
  },
  overlayText: {
    color: PAL.white,
    fontSize: 13,
    fontWeight: "700",
    lineHeight: 18,
    fontStyle: "italic",
    textShadowColor: "rgba(0,0,0,0.9)",
    textShadowOffset: { width: 0, height: 1 },
    textShadowRadius: 6,
  },
  sunrise: {
    position: "absolute",
    alignSelf: "center",
    width: "120%",
    height: "55%",
    borderRadius: 999,
    backgroundColor: "rgba(255, 138, 101, 0.35)",
  },
  sunCore: {
    position: "absolute",
    alignSelf: "center",
    width: 56,
    height: 56,
    borderRadius: 28,
    backgroundColor: PAL.goldSoft,
    shadowColor: PAL.gold,
    shadowOpacity: 0.9,
    shadowRadius: 24,
  },
  step: {
    position: "absolute",
    height: 10,
    borderRadius: 4,
    borderWidth: 1,
  },
  traveler: {
    position: "absolute",
    width: 10,
    height: 18,
    borderRadius: 5,
    backgroundColor: PAL.white,
    opacity: 0.9,
  },
  half: { flex: 1, position: "relative" },
  chaosGlow: { ...StyleSheet.absoluteFillObject },
  calmSun: {
    position: "absolute",
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: PAL.goldSoft,
    opacity: 0.85,
  },
  calmLine: {
    position: "absolute",
    height: 3,
    borderRadius: 2,
    backgroundColor: PAL.calm,
    opacity: 0.8,
  },
  plant: {
    position: "absolute",
    width: 14,
    height: 22,
    borderTopLeftRadius: 10,
    borderTopRightRadius: 10,
    backgroundColor: "#34D399",
  },
  centerFigure: {
    position: "absolute",
    alignSelf: "center",
    width: 12,
    height: 22,
    borderRadius: 6,
    backgroundColor: PAL.white,
    zIndex: 2,
  },
  centerLine: {
    position: "absolute",
    top: 0,
    bottom: 0,
    left: "50%",
    width: 2,
    marginLeft: -1,
    backgroundColor: "rgba(255,255,255,0.15)",
    zIndex: 1,
  },
  treeCanopy: {
    position: "absolute",
    alignSelf: "center",
    width: "70%",
    height: "42%",
    borderRadius: 999,
    backgroundColor: "rgba(45, 212, 191, 0.35)",
    borderWidth: 1,
    borderColor: "rgba(94, 234, 212, 0.4)",
  },
  treeTrunk: {
    position: "absolute",
    alignSelf: "center",
    width: 22,
    backgroundColor: "#3D2E1F",
    borderRadius: 4,
  },
  root: {
    position: "absolute",
    backgroundColor: PAL.teal,
    borderRadius: 2,
    shadowOpacity: 0.8,
    shadowRadius: 8,
  },
  rootGlow: {
    position: "absolute",
    left: "10%",
    right: "10%",
    height: 30,
    borderRadius: 15,
    backgroundColor: "rgba(45, 212, 191, 0.25)",
  },
  mountain: {
    position: "absolute",
    bottom: 0,
    left: "-10%",
    right: "-10%",
    backgroundColor: "#1E3A5F",
    transform: [{ skewX: "-12deg" }],
    borderTopLeftRadius: 8,
    borderTopRightRadius: 40,
  },
  snowDrift: {
    position: "absolute",
    left: "15%",
    width: "55%",
    height: 40,
    borderRadius: 20,
    backgroundColor: "rgba(255,255,255,0.12)",
  },
  climber: {
    position: "absolute",
    width: 8,
    height: 14,
    borderRadius: 4,
    backgroundColor: PAL.white,
    opacity: 0.85,
  },
  blurBlock: {
    position: "absolute",
    width: 48,
    height: 28,
    borderRadius: 6,
    backgroundColor: "#64748B",
  },
  focusCube: {
    width: "52%",
    aspectRatio: 1,
    borderRadius: 12,
    borderWidth: 2,
    borderColor: "rgba(94, 234, 212, 0.7)",
    backgroundColor: "rgba(6, 20, 30, 0.75)",
    overflow: "hidden",
  },
  cubeInner: { flex: 1, alignItems: "center", justifyContent: "center" },
  cubeDesk: {
    width: "55%",
    height: 8,
    borderRadius: 2,
    backgroundColor: "rgba(94, 234, 212, 0.5)",
    marginTop: 20,
  },
  cubeGlow: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: "rgba(45, 212, 191, 0.08)",
  },
  brainShell: { alignItems: "center", justifyContent: "center" },
  brainPulse: {
    position: "absolute",
    borderRadius: 999,
    borderWidth: 2,
    borderColor: "rgba(34, 211, 238, 0.5)",
  },
  brainCore: {
    width: 48,
    height: 56,
    borderRadius: 28,
    backgroundColor: "rgba(34, 211, 238, 0.35)",
    borderWidth: 2,
    borderColor: PAL.cyan,
  },
  lightWave: {
    position: "absolute",
    left: "-10%",
    right: "-10%",
    height: "40%",
    borderTopLeftRadius: 999,
    borderTopRightRadius: 999,
    backgroundColor: "rgba(34, 211, 238, 0.45)",
  },
  surfSilhouette: {
    position: "absolute",
    width: 14,
    height: 20,
    borderRadius: 7,
    backgroundColor: PAL.white,
    opacity: 0.9,
  },
  darkPast: {
    position: "absolute",
    top: 0,
    bottom: 0,
    left: 0,
    backgroundColor: "#0A0610",
  },
  brightFuture: {
    position: "absolute",
    top: 0,
    bottom: 0,
    backgroundColor: "rgba(255, 200, 120, 0.15)",
  },
  doorFrame: {
    position: "absolute",
    alignSelf: "center",
    width: "22%",
    borderWidth: 3,
    borderColor: "rgba(245, 197, 66, 0.6)",
    borderRadius: 4,
    backgroundColor: "rgba(255, 220, 150, 0.2)",
  },
  walker: {
    position: "absolute",
    width: 12,
    height: 24,
    borderRadius: 6,
    backgroundColor: PAL.white,
  },
  chain: {
    position: "absolute",
    width: 28,
    height: 8,
    borderRadius: 4,
    borderWidth: 2,
    borderColor: "#64748B",
    opacity: 0.6,
  },
  cityGlow: {
    position: "absolute",
    width: 60,
    height: 40,
    borderRadius: 8,
    backgroundColor: "rgba(94, 234, 212, 0.2)",
  },
  messBlock: {
    position: "absolute",
    width: 36,
    height: 20,
    borderRadius: 4,
    backgroundColor: "#4B5563",
    opacity: 0.7,
  },
  phoneGlow: {
    position: "absolute",
    width: 22,
    height: 36,
    borderRadius: 4,
    backgroundColor: "rgba(251, 113, 133, 0.4)",
  },
  windowSun: {
    position: "absolute",
    width: 50,
    height: 50,
    borderRadius: 6,
    backgroundColor: "rgba(253, 230, 138, 0.35)",
    borderWidth: 1,
    borderColor: "rgba(253, 230, 138, 0.5)",
  },
  minDesk: {
    position: "absolute",
    width: 56,
    height: 10,
    borderRadius: 2,
    backgroundColor: "rgba(94, 234, 212, 0.4)",
  },
  splitDivider: {
    position: "absolute",
    top: 0,
    bottom: 0,
    left: "50%",
    width: 2,
    marginLeft: -1,
    backgroundColor: "rgba(255,255,255,0.2)",
  },
  horizonGlow: {
    position: "absolute",
    alignSelf: "center",
    width: "100%",
    height: "50%",
    backgroundColor: "rgba(45, 212, 191, 0.2)",
  },
  pathLine: {
    position: "absolute",
    left: "20%",
    right: "20%",
    height: 3,
    borderRadius: 2,
    backgroundColor: PAL.teal,
    opacity: 0.6,
  },
});
